import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Box, Fab, Menu, MenuItem, ListItemIcon, ListItemText, Divider, Badge } from '@mui/material'
import LocalDrinkIcon from '@mui/icons-material/LocalDrink'
import CloseIcon from '@mui/icons-material/Close'
import LocalBarIcon from '@mui/icons-material/LocalBar'
import FavoriteIcon from '@mui/icons-material/Favorite'
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder'
import StorefrontIcon from '@mui/icons-material/Storefront'
import FilterListIcon from '@mui/icons-material/FilterList'
import RouletteWheelIcon from '../Brewlette/RouletteWheelIcon'
import { Beer } from '../../state/types'
import { useBeer } from '../../state/BeerContext'
import { clearGlobalSelection, subscribeToSelection } from '../BeerCard/BeerCardContainer'
import { useBreweryActionBar } from '../../state/hooks/useBreweryActionBar'
import { useBreweryDetail } from '../../state/hooks/useBreweryDetail'
import { useFilters } from '../../state/hooks/useFilters'
import { useToast } from '../../state/providers/ToastProvider'
import {
  buildBeerFavouritedEvent,
  buildBeerTastingListAddedEvent,
  buildBeerTastingListRemovedEvent,
  buildBeerUnfavouritedEvent,
  useAnalyticsService,
} from '../../services/analytics'
import styles from './ActionControlsFab.module.css'

interface ActionControlsFabProps {
  showBrewlette?: boolean
  onBrewletteClick?: () => void
}

function buildFabAriaLabel(params: {
  isBreweryOpen: boolean
  breweryName: string | null
  selectionCount: number
  showBrewlette: boolean
}): string {
  const { isBreweryOpen, breweryName, selectionCount, showBrewlette } = params
  const parts: string[] = []

  if (isBreweryOpen && breweryName) {
    parts.push('Brewery actions')
  }
  if (selectionCount > 0) {
    parts.push(`Selection: ${selectionCount}`)
  }
  if (showBrewlette) {
    parts.push('Brewlette')
  }

  return parts.length > 0 ? parts.join(', ') : 'Actions'
}

function buildBreweryMenuItems(params: {
  isBreweryOpen: boolean
  breweryName: string | null
  isActiveBreweryFilter: boolean
  onOpenBreweryPage: () => void
  onToggleBreweryFilter: () => void
}): React.ReactNode[] {
  const { isBreweryOpen, breweryName, isActiveBreweryFilter, onOpenBreweryPage, onToggleBreweryFilter } = params

  if (!isBreweryOpen || !breweryName) {
    return []
  }

  return [
    <MenuItem key="brewery-page" onClick={onOpenBreweryPage} aria-label="Open brewery page">
      <ListItemIcon sx={{ color: 'var(--secondary-colour)', minWidth: 38 }}>
        <StorefrontIcon sx={{ fontSize: 22 }} />
      </ListItemIcon>
      <ListItemText primary="Brewery Page" />
    </MenuItem>,
    <MenuItem
      key="brewery-filter"
      onClick={onToggleBreweryFilter}
      aria-label={isActiveBreweryFilter ? 'Clear brewery filter' : 'Filter by brewery'}
    >
      <ListItemIcon sx={{ color: 'var(--secondary-colour)', minWidth: 38 }}>
        <FilterListIcon sx={{ fontSize: 22 }} />
      </ListItemIcon>
      <ListItemText primary={isActiveBreweryFilter ? 'Clear Filter' : 'Filter Brewery'} />
    </MenuItem>,
  ]
}

function buildSelectionMenuItems(params: {
  selectionCount: number
  beersToToggleListCount: number
  shouldAddToList: boolean
  beersToToggleFavouriteCount: number
  shouldFavourite: boolean
  onClearSelection: () => void
  onToggleTastingList: () => void
  onToggleFavourite: () => void
}): React.ReactNode[] {
  const {
    selectionCount,
    beersToToggleListCount,
    shouldAddToList,
    beersToToggleFavouriteCount,
    shouldFavourite,
    onClearSelection,
    onToggleTastingList,
    onToggleFavourite,
  } = params

  if (selectionCount <= 0) {
    return []
  }

  return [
    <MenuItem key="clear-selection" onClick={onClearSelection} aria-label="Clear selection">
      <ListItemIcon sx={{ color: 'var(--secondary-colour)', minWidth: 38 }}>
        <CloseIcon sx={{ fontSize: 22 }} />
      </ListItemIcon>
      <ListItemText primary="Clear selection" />
    </MenuItem>,
    <MenuItem
      key="tasting-list"
      onClick={onToggleTastingList}
      aria-label={shouldAddToList ? 'Add to Tasting List' : 'Remove from Tasting List'}
    >
      <ListItemIcon sx={{ color: 'var(--secondary-colour)', minWidth: 38 }}>
        <LocalBarIcon sx={{ fontSize: 22 }} />
      </ListItemIcon>
      <ListItemText
        primary={`${shouldAddToList ? 'Add' : 'Remove'} ${beersToToggleListCount} ${shouldAddToList ? 'to Tasting List' : 'from Tasting List'}`}
      />
    </MenuItem>,
    <MenuItem
      key="favourites"
      onClick={onToggleFavourite}
      aria-label={shouldFavourite ? 'Add to Favourites' : 'Remove from Favourites'}
    >
      <ListItemIcon sx={{ color: 'var(--secondary-colour)', minWidth: 38 }}>
        {shouldFavourite ? <FavoriteBorderIcon sx={{ fontSize: 22 }} /> : <FavoriteIcon sx={{ fontSize: 22 }} />}
      </ListItemIcon>
      <ListItemText
        primary={`${shouldFavourite ? 'Add' : 'Remove'} ${beersToToggleFavouriteCount} ${shouldFavourite ? 'to Favourites' : 'from Favourites'}`}
      />
    </MenuItem>,
  ]
}

function buildBrewletteMenuItems(params: {
  showBrewlette: boolean
  onBrewletteClick?: () => void
  onBrewlette: () => void
}): React.ReactNode[] {
  const { showBrewlette, onBrewletteClick, onBrewlette } = params

  if (!showBrewlette || !onBrewletteClick) {
    return []
  }

  return [
    <MenuItem key="brewlette" onClick={onBrewlette} aria-label="Open Brewlette">
      <ListItemIcon sx={{ color: 'var(--secondary-colour)', minWidth: 38 }}>
        <RouletteWheelIcon size={22} />
      </ListItemIcon>
      <ListItemText primary="Brewlette" />
    </MenuItem>,
  ]
}

function buildNoActionsMenuItems(hasAnyActions: boolean): React.ReactNode[] {
  if (hasAnyActions) {
    return []
  }

  return [
    <MenuItem key="no-actions" disabled aria-label="No actions available">
      <ListItemText primary="Select a beer to see actions" />
    </MenuItem>,
  ]
}

const ActionControlsFab: React.FC<ActionControlsFabProps> = ({ showBrewlette = false, onBrewletteClick }) => {
  const [selectedBeerIds, setSelectedBeerIds] = useState<Set<string>>(new Set())
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const menuOpen = Boolean(anchorEl)
  const fabRef = useRef<HTMLButtonElement | null>(null)

  const { showToast } = useToast()

  const { isOpen: isBreweryOpen, breweryName, closeBreweryActions } = useBreweryActionBar()
  const { openBreweryDetail } = useBreweryDetail()
  const { filters, setBreweryFilter } = useFilters()

  const {
    state: { beers },
    toggleTastingList,
    toggleFavorite,
    userInteractions: { tastingList, favorites },
  } = useBeer()

  const analyticsService = useAnalyticsService()
  const selectionCount = selectedBeerIds.size

  useEffect(() => {
    const unsubscribe = subscribeToSelection((ids) => {
      setSelectedBeerIds(ids)
    })

    return () => {
      unsubscribe()
    }
  }, [beers.length])

  const selectedBeers: Beer[] = useMemo(() => {
    return Array.from(selectedBeerIds)
      .map((id) => beers.find((b) => b.id === id))
      .filter((b): b is Beer => b !== undefined)
  }, [beers, selectedBeerIds])

  const beersInList = selectedBeers.filter((beer) => tastingList.includes(beer.id))
  const beersNotInList = selectedBeers.filter((beer) => !tastingList.includes(beer.id))
  const shouldAddToList = beersNotInList.length >= beersInList.length
  const beersToToggleList = shouldAddToList ? beersNotInList : beersInList

  const beersFavourited = selectedBeers.filter((beer) => favorites.includes(beer.id))
  const beersNotFavourited = selectedBeers.filter((beer) => !favorites.includes(beer.id))
  const shouldFavourite = beersNotFavourited.length >= beersFavourited.length
  const beersToToggleFavourite = shouldFavourite ? beersNotFavourited : beersFavourited

  const isActiveBreweryFilter = Boolean(breweryName) && filters.breweryFilter === breweryName

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const closeBreweryActionsIfOpen = () => {
    if (!isBreweryOpen) {
      return
    }

    closeBreweryActions()
  }

  const handleMenuOnClose = () => {
    handleMenuClose()
    closeBreweryActionsIfOpen()
  }

  const handleFabClick = (event: React.MouseEvent<HTMLElement>) => {
    if (menuOpen) {
      closeBreweryActionsIfOpen()
      handleMenuClose()
      return
    }

    setAnchorEl(event.currentTarget)
  }

  useEffect(() => {
    if (!isBreweryOpen) {
      return
    }

    if (menuOpen) {
      return
    }

    if (fabRef.current) {
      setAnchorEl(fabRef.current)
    }
  }, [isBreweryOpen, menuOpen])

  const handleClearSelection = () => {
    clearGlobalSelection()
    showToast('Selection cleared')
    closeBreweryActionsIfOpen()
    handleMenuClose()
  }

  const handleToggleTastingList = () => {
    if (selectedBeers.length === 0) {
      return
    }

    if (beersToToggleList.length === 0) {
      return
    }

    beersToToggleList.forEach((beer) => {
      const event = shouldAddToList ? buildBeerTastingListAddedEvent(beer) : buildBeerTastingListRemovedEvent(beer)
      analyticsService.trackEvent(event)
    })

    beersToToggleList.forEach((beer) => toggleTastingList(beer.id))
    showToast(
      shouldAddToList
        ? `Added ${beersToToggleList.length} beer${beersToToggleList.length === 1 ? '' : 's'} to Tasting List`
        : `Removed ${beersToToggleList.length} beer${beersToToggleList.length === 1 ? '' : 's'} from Tasting List`
    )
    closeBreweryActionsIfOpen()
    handleMenuClose()
    clearGlobalSelection()
  }

  const handleToggleFavourite = () => {
    if (selectedBeers.length === 0) {
      return
    }

    if (beersToToggleFavourite.length === 0) {
      return
    }

    beersToToggleFavourite.forEach((beer) => {
      const event = shouldFavourite ? buildBeerFavouritedEvent(beer) : buildBeerUnfavouritedEvent(beer)
      analyticsService.trackEvent(event)
    })

    beersToToggleFavourite.forEach((beer) => toggleFavorite(beer.id))
    showToast(
      shouldFavourite
        ? `Added ${beersToToggleFavourite.length} beer${beersToToggleFavourite.length === 1 ? '' : 's'} to Favourites`
        : `Removed ${beersToToggleFavourite.length} beer${beersToToggleFavourite.length === 1 ? '' : 's'} from Favourites`
    )
    closeBreweryActionsIfOpen()
    handleMenuClose()
    clearGlobalSelection()
  }

  const handleBrewlette = () => {
    if (!onBrewletteClick) {
      return
    }

    handleMenuClose()
    showToast('Opening Brewlette')
    closeBreweryActionsIfOpen()
    onBrewletteClick()
  }

  const handleOpenBreweryPage = () => {
    if (!breweryName) {
      return
    }

    const didOpen = openBreweryDetail(breweryName)
    if (!didOpen) {
      setBreweryFilter(breweryName)
    }

    showToast('Opening brewery')

    closeBreweryActions()
    handleMenuClose()
  }

  const handleToggleBreweryFilter = () => {
    if (!breweryName) {
      return
    }

    if (isActiveBreweryFilter) {
      setBreweryFilter('')
      showToast('Brewery filter cleared')
    } else {
      setBreweryFilter(breweryName)
      showToast(`Filtered by ${breweryName}`)
    }

    closeBreweryActions()
    handleMenuClose()
  }

  const fabAriaLabel = buildFabAriaLabel({
    isBreweryOpen,
    breweryName,
    selectionCount,
    showBrewlette,
  })

  const hasAnyActions = selectionCount > 0 || showBrewlette || isBreweryOpen

  const menuItems: React.ReactNode[] = [
    ...buildBreweryMenuItems({
      isBreweryOpen,
      breweryName,
      isActiveBreweryFilter,
      onOpenBreweryPage: handleOpenBreweryPage,
      onToggleBreweryFilter: handleToggleBreweryFilter,
    }),
    ...(isBreweryOpen && breweryName && (selectionCount > 0 || showBrewlette)
      ? [
          <Divider
            key="brewery-divider"
            sx={{
              my: 0.5,
              borderColor: 'color-mix(in srgb, var(--secondary-colour) 28%, transparent)',
            }}
          />,
        ]
      : []),
    ...buildNoActionsMenuItems(hasAnyActions),
    ...buildSelectionMenuItems({
      selectionCount,
      beersToToggleListCount: beersToToggleList.length,
      shouldAddToList,
      beersToToggleFavouriteCount: beersToToggleFavourite.length,
      shouldFavourite,
      onClearSelection: handleClearSelection,
      onToggleTastingList: handleToggleTastingList,
      onToggleFavourite: handleToggleFavourite,
    }),
    ...buildBrewletteMenuItems({
      showBrewlette,
      onBrewletteClick,
      onBrewlette: handleBrewlette,
    }),
  ]

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: '72px',
        right: '16px',
        zIndex: 1010,
      }}
    >
      <Badge
        badgeContent={selectionCount}
        color="default"
        invisible={selectionCount <= 0}
        overlap="circular"
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{
          '& .MuiBadge-badge': {
            boxSizing: 'border-box',
            right: 3,
            top: 0,
            height: 30,
            minWidth: 30,
            padding: '0 4px',
            fontSize: '1rem',
            lineHeight: 1.2,
            opacity: 1,
            zIndex: 1050,
            borderRadius: '50%',
            pointerEvents: 'none',
          },
        }}
        classes={{
          badge: styles.badgeContent,
        }}
      >
        <Fab
          color="primary"
          ref={fabRef}
          onClick={handleFabClick}
          aria-label={fabAriaLabel}
          data-tour="fab-button"
          size="medium"
          sx={{
            backgroundColor: 'var(--primary-colour)',
            color: 'var(--secondary-colour)',
            border: '1px solid color-mix(in srgb, var(--secondary-colour) 42%, transparent)',
            boxShadow: '0 10px 26px rgba(0, 0, 0, 0.45)',
            borderRadius: '12px',
            width: 48,
            height: 48,
            minHeight: 48,
            '&:hover': {
              backgroundColor: 'color-mix(in srgb, var(--primary-colour) 84%, black)',
            },
          }}
        >
          <LocalDrinkIcon sx={{ fontSize: 28 }} />
        </Fab>
      </Badge>

      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuOnClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        keepMounted
        sx={{
          '& .MuiMenu-paper': {
            ml: '-4px',
          },
        }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 260,
            borderRadius: 2,
            backgroundColor: 'var(--primary-colour)',
            border: '1px solid color-mix(in srgb, var(--secondary-colour) 42%, transparent)',
            backdropFilter: 'blur(6px)',
            color: 'var(--secondary-colour)',
          },
        }}
      >
        {menuItems.length > 0 ? menuItems : null}
      </Menu>
    </Box>
  )
}

export default ActionControlsFab
